import { supabase } from '@/integrations/supabase/client';

export interface ConversationFeedbackData {
  conversation_id: string;
  agent_id?: string;
  rating: 'positive' | 'negative';
  feedback_text?: string;
  user_agent?: string;
  ip_address?: string;
}

export class ConversationFeedbackService {
  static async submitFeedback(feedbackData: ConversationFeedbackData) {
    try {
      const { data, error } = await supabase
        .from('conversation_feedback')
        .insert([{
          conversation_id: feedbackData.conversation_id,
          agent_id: feedbackData.agent_id,
          rating: feedbackData.rating,
          feedback_text: feedbackData.feedback_text,
          user_agent: feedbackData.user_agent || navigator.userAgent,
          ip_address: feedbackData.ip_address, // This would need to be set server-side in a real app
        }])
        .select()
        .single();

      return { data, error };
    } catch (err) {
      console.error('Error submitting conversation feedback:', err);
      return { data: null, error: err };
    }
  }

  static async getFeedbackByConversation(conversationId: string) {
    try {
      const { data, error } = await supabase
        .from('conversation_feedback')
        .select('*')
        .eq('conversation_id', conversationId)
        .single();

      return { data, error };
    } catch (err) {
      console.error('Error fetching conversation feedback:', err);
      return { data: null, error: err };
    }
  }

  static async getFeedbackByAgentPage(agentPageId: string, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('conversation_feedback')
        .select('*')
        .eq('agent_page_id', agentPageId)
        .order('created_at', { ascending: false })
        .limit(limit);

      return { data, error };
    } catch (err) {
      console.error('Error fetching agent page feedback:', err);
      return { data: null, error: err };
    }
  }

  static async getFeedbackStats(agentPageId?: string) {
    try {
      let query = supabase
        .from('conversation_feedback')
        .select('rating');

      if (agentPageId) {
        query = query.eq('agent_page_id', agentPageId);
      }

      const { data, error } = await query;

      if (error) {
        return { data: null, error };
      }

      const stats = {
        total: data.length,
        positive: data.filter(f => f.rating === 'positive').length,
        negative: data.filter(f => f.rating === 'negative').length,
        positivePercentage: data.length > 0 ? Math.round((data.filter(f => f.rating === 'positive').length / data.length) * 100) : 0,
      };

      return { data: stats, error: null };
    } catch (err) {
      console.error('Error fetching feedback stats:', err);
      return { data: null, error: err };
    }
  }
}
